import type {
    BwWelfareDay,
    BwWelfareMetrics,
    BwWelfareResponse,
    BwWelfareTrendDay,
    BwWelfareWeek,
} from '@/services/types';

import type { ApiWire } from '../shared/types';

import type {
    BodyWoundsData,
    DailyBodyWoundsData,
    DailyWelfareData,
    FullWelfareData,
    WeeklyWelfareData,
    WelfareData,
    WelfareMetrics,
    WelfareTrendData,
} from './types';

type ApiWelfareValues = { [K in keyof BwWelfareMetrics]?: number | null };

function mapWelfareMetricsToDomain(metrics: ApiWelfareValues): WelfareMetrics {
    return {
        averageWoundSize: metrics.averageWoundSize ?? null,
        bigWound: metrics.bigWound ?? null,
        mediumWound: metrics.mediumWound ?? null,
        moreThanOneWound: metrics.moreThanOneWound ?? null,
        noWound: metrics.noWound ?? null,
        oneWound: metrics.oneWound ?? null,
        smallWound: metrics.smallWound ?? null,
        wound: metrics.wound ?? null,
        skinSpeckles: metrics.skinSpeckles ?? null,
        matureJaw: metrics.matureJaw ?? null,
        notSuperior: metrics.notSuperior ?? null,
    };
}

function mapDailyWelfareDataToDomain(data: ApiWire<BwWelfareDay>): DailyWelfareData {
    return {
        ...mapWelfareMetricsToDomain(data),
        day: data.day,
        count: data.count ?? null,
        total: data.total ?? null,
    };
}

function mapWelfareTrendDataToDomain(data: ApiWire<BwWelfareTrendDay>): WelfareTrendData {
    return { ...mapWelfareMetricsToDomain(data), day: data.day };
}

function mapWeeklyWelfareDataToDomain(data: ApiWire<BwWelfareWeek>): WeeklyWelfareData {
    return {
        ...mapWelfareMetricsToDomain(data),
        isoWeek: data.isoWeek,
        isoYear: data.isoYear,
    };
}

function mapWelfareDataToDomain(data: ApiWire<BwWelfareResponse>): WelfareData {
    return {
        lastEstimate: mapWelfareMetricsToDomain(data.lastEstimate),
        lastDay: data.lastDay,
        days: data.days.map(mapDailyWelfareDataToDomain),
        slopePerDay: mapWelfareMetricsToDomain(data.slopePerDay),
        trend: data.trend.map(mapWelfareTrendDataToDomain),
        weekly: data.weekly.map(mapWeeklyWelfareDataToDomain),
    };
}

/** The wound fields of a welfare day, as the wound charts read them. */
function toBodyWoundsDay(
    day: Pick<DailyWelfareData, keyof DailyBodyWoundsData>
): DailyBodyWoundsData {
    return {
        day: day.day,
        wound: day.wound,
        averageWoundSize: day.averageWoundSize,
        moreThanOneWound: day.moreThanOneWound,
        noWound: day.noWound,
        oneWound: day.oneWound,
        smallWound: day.smallWound,
        mediumWound: day.mediumWound,
        bigWound: day.bigWound,
        count: day.count,
        total: day.total,
    };
}

/**
 * The wound series is the welfare series' wound fields: BW welfare averages each field on its
 * own, so the days carry exactly what a separate wound-only series would.
 */
function toBodyWoundsData(welfareData: WelfareData): BodyWoundsData {
    return {
        days: welfareData.days.map(toBodyWoundsDay),
        lastEstimate: toBodyWoundsDay({
            ...welfareData.lastEstimate,
            day: welfareData.lastDay,
            count: welfareData.days.at(-1)?.count ?? null,
            total: welfareData.days.at(-1)?.total ?? null,
        }),
    };
}

const EMPTY_METRICS: WelfareMetrics = mapWelfareMetricsToDomain({});

const EMPTY_WELFARE_DATA: WelfareData = {
    lastEstimate: EMPTY_METRICS,
    lastDay: '',
    days: [],
    slopePerDay: EMPTY_METRICS,
    trend: [],
    weekly: [],
};

/** Empty series, with every value null, until the response arrives. */
export function mapFullWelfareResponseToDomain(
    response?: ApiWire<BwWelfareResponse>
): FullWelfareData {
    const welfareData = response ? mapWelfareDataToDomain(response) : EMPTY_WELFARE_DATA;

    return { welfareData, bodyWoundsData: toBodyWoundsData(welfareData) };
}
